import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Crosshair, ArrowUpRight, Clock, IndianRupee, Package, TrendingUp } from "lucide-react";
import { formatGeneratedAt } from "../lib/datetime";

function WhitespaceCard({ opp, idx }) {
  return (
    <Link
      to={`/opportunities/${opp.id}`}
      data-testid={`whitespace-card-${opp.id}`}
      className="bm-card fade-up p-5 flex flex-col gap-3 group hover:border-bm-cyan/60 transition-colors"
      style={{ animationDelay: `${Math.min(idx * 40, 500)}ms` }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="label-mono text-bm-cyan border border-bm-cyan/50 px-1.5 py-0.5" data-testid={`whitespace-badge-${opp.id}`}>WHITESPACE</span>
          <span className="label-mono text-bm-dim">{opp.category}</span>
          {opp.subcategory && <span className="label-mono text-bm-amber">· {opp.subcategory}</span>}
        </div>
        <div className={`px-2 py-1 border font-mono text-xs ${opp.scores?.overall >= 80 ? "border-bm-green/50 text-bm-green" : "border-bm-amber/50 text-bm-amber"}`}>
          {opp.scores?.overall}% CONV
        </div>
      </div>

      <h3 className="font-heading text-xl font-black tracking-tight text-white group-hover:text-bm-cyan transition-colors">
        {opp.product_nomenclature || opp.product_name}
      </h3>
      {opp.tagline && <p className="text-sm text-bm-muted">{opp.tagline}</p>}
      <p className="text-sm leading-relaxed text-bm-text/85 line-clamp-3">{opp.why_now}</p>

      <div className="grid grid-cols-2 gap-2 border-t border-white/5 pt-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-3.5 h-3.5 text-bm-cyan" />
          <div>
            <div className="label-mono text-bm-dim">BEH</div>
            <div className={`font-mono text-sm ${opp.scores?.behaviour >= 80 ? "text-bm-green" : "text-bm-amber"}`}>{opp.scores?.behaviour}%</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Crosshair className="w-3.5 h-3.5 text-bm-amber" />
          <div>
            <div className="label-mono text-bm-dim">PRD</div>
            <div className={`font-mono text-sm ${opp.scores?.product < 72 ? "text-bm-amber" : "text-bm-green"}`}>{opp.scores?.product}%</div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between text-xs font-mono pt-2 border-t border-white/5">
        <div className="flex items-center gap-3 text-bm-muted">
          <span className="flex items-center gap-1"><IndianRupee className="w-3 h-3" />{opp.price_inr}</span>
          <span className="flex items-center gap-1"><Package className="w-3 h-3" />{opp.pack_size}</span>
        </div>
        <ArrowUpRight className="w-4 h-4 text-bm-dim group-hover:text-bm-cyan" />
      </div>

      <div className="flex items-center gap-1 text-[10px] font-mono text-bm-dim">
        <Clock className="w-3 h-3" />Generated {formatGeneratedAt(opp.generated_at || opp.created_at)}
      </div>
    </Link>
  );
}

export default function Whitespace() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.opportunities({ limit: 100 }).then((d) => {
      const whitespace = (d.items || []).filter((o) => o.ux_label === "Whitespace");
      // Sort whitespace by behaviour-strong / product-nascent (the canonical whitespace signature)
      whitespace.sort((a, b) => (b.scores?.behaviour || 0) - (a.scores?.behaviour || 0));
      setItems(whitespace);
      setLoading(false);
    });
  }, []);

  return (
    <div className="space-y-5">
      <div className="border-b border-white/10 pb-4">
        <div className="label-mono text-bm-cyan flex items-center gap-2">
          <Crosshair className="w-3.5 h-3.5" />WHITESPACE OPPORTUNITIES
        </div>
        <h1 className="font-heading text-4xl font-black tracking-tight mt-1" data-testid="page-title">
          Where nobody's playing yet.
        </h1>
        <p className="text-sm text-bm-muted mt-2 max-w-3xl">
          Opportunities where the <span className="text-bm-cyan">behaviour signal is strong</span> but
          <span className="text-bm-amber"> product validation is still nascent</span> — the canonical
          whitespace signature. First-mover advantage live.
        </p>
      </div>

      {loading && <div className="font-mono text-xs text-bm-cyan scanline p-6">FINDING WHITESPACE...</div>}

      {!loading && items.length === 0 && (
        <div className="text-center py-12 font-mono text-sm text-bm-muted">
          No whitespace opportunities right now — every category has product validation already.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="whitespace-grid">
        {items.map((opp, i) => <WhitespaceCard key={opp.id} opp={opp} idx={i} />)}
      </div>
    </div>
  );
}
